// WeatherBadge — render del clima de un día en la app.
// Comportamiento adaptativo según `daysAway`:
//   - Forecast (próximos 16 días): muestra temp + condición + prob. lluvia
//   - Historical (>16 días): muestra promedio histórico con disclaimer
import { useWeather, weatherInfo } from "../hooks/useWeather";
import { Cloud, Loader2 } from "lucide-react";

function ClothingHint({ tmax, tmin, rain }) {
  const hints = [];
  if (tmax >= 28) hints.push("☀️ Ropa ligera, agua a mano");
  else if (tmax >= 22) hints.push("👕 Ligero pero llevar una capa fina");
  else if (tmax >= 16) hints.push("🧥 Capas + chaqueta liviana");
  else if (tmax >= 10) hints.push("🧥 Abrigo, en la tarde refresca");
  else hints.push("🧤 Frío — capas + bufanda");
  if (rain >= 5 || (rain > 0 && tmax < 18)) hints.push("☔ Paraguas o cortavientos");
  if (tmin <= 8 && tmax - tmin >= 10) hints.push("🌅 Amanecer fresco, refuerzo en mañana");
  if (!hints.length) return null;
  return (
    <ul className="text-[11px] text-[hsl(var(--ink-soft))] leading-relaxed mt-1.5 space-y-0.5">
      {hints.map((h, i) => <li key={i}>{h}</li>)}
    </ul>
  );
}

export default function WeatherBadge({ lat, lng, date, city, expanded = false }) {
  const { loading, data, error, daysAway } = useWeather({ lat, lng, date });

  if (!lat || !lng || !date) return null;

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-[hsl(var(--muted))]" data-testid="weather-loading">
        <Loader2 size={12} className="animate-spin" /> Clima…
      </div>
    );
  }
  if (error || !data) {
    return null; // silenciamos errores para no romper la UI
  }

  const wi = weatherInfo(data.code);
  const isHistorical = data.kind === "historical";
  const isFar = daysAway > 16;
  const isReliable = daysAway >= 0 && daysAway <= 7;

  // Compact chip mode (header de día)
  if (!expanded) {
    return (
      <div
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[hsl(var(--bg-card))] border border-[hsl(var(--line))] text-xs"
        data-testid={`weather-${date}`}
        title={isHistorical ? `Promedio histórico (${data.sampleYears} años)` : wi.label}
      >
        <span className="text-base leading-none">{wi.icon}</span>
        <span className="font-bold tabular-nums">{data.tmax}°<span className="opacity-50 mx-0.5">/</span>{data.tmin}°</span>
        {isHistorical && (
          <span className="text-[9px] uppercase tracking-wider text-[hsl(var(--muted))]">histórico</span>
        )}
        {!isHistorical && !isReliable && (
          <span className="text-[9px] uppercase tracking-wider text-[hsl(var(--muted))]">preliminar</span>
        )}
      </div>
    );
  }

  // Expanded card
  return (
    <div className="card-paper p-4" data-testid={`weather-expanded-${date}`}>
      <div className="flex items-center gap-3">
        <span className="text-4xl leading-none">{wi.icon}</span>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted))] font-semibold">
            Clima · {city}
            {isHistorical && <span className="ml-1">· promedio histórico</span>}
            {!isHistorical && !isReliable && <span className="ml-1">· preliminar</span>}
          </p>
          <p className="text-2xl font-bold leading-tight tabular-nums">
            {data.tmax}° <span className="opacity-50 mx-1 font-normal">/</span> {data.tmin}°
          </p>
          <p className="text-xs text-[hsl(var(--ink-soft))] mt-0.5">{wi.label}</p>
        </div>
        {!isHistorical && (data.rainProb || data.rain) ? (
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted))]">Lluvia</p>
            <p className="text-sm font-bold">{data.rainProb ? `${data.rainProb}%` : `${data.rain}mm`}</p>
          </div>
        ) : null}
      </div>

      <ClothingHint tmax={data.tmax} tmin={data.tmin} rain={data.rain} />

      {isHistorical && (
        <p className="text-[10px] text-[hsl(var(--muted))] mt-2 leading-tight">
          Faltan {daysAway} días — promedio de últimos {data.sampleYears} años para esta fecha.
          El forecast preciso se mostrará 16 días antes.
        </p>
      )}
      {!isHistorical && !isReliable && (
        <p className="text-[10px] text-[hsl(var(--muted))] mt-2 leading-tight">
          Forecast a {daysAway} días vista — se afinará al acercarse.
        </p>
      )}
    </div>
  );
}
